import * as THREE from 'three'
import { bakeVinylTexture } from '../utils/VinylTexture'
import groovesChunk from '../shaders/grooves.glsl'
import paperChunk from '../shaders/paper.glsl'

/**
 * MeshPhysicalMaterial for the record model. The colour comes from a texture
 * baked once per record (utils/VinylTexture.js), sampled by object-space position so it
 * doesn't care about the model's UV atlas; the model's normal + ORM maps are passed in.
 * Per frame, only the grooves are computed: a faint ridge ripple + shading (needs the
 * screen pixel size), and the specular stretched radially - the grooves themselves are
 * below texture resolution, what reads as vinyl is that anisotropic sheen.
 *
 * @param {number} scale  turns the mesh's local xy into disc units (-0.5..0.5 across the
 *                        record): the mesh's scale over the record's diameter
 * @param {THREE.Texture} [environmentMap]  PMREM environment for the vinyl to reflect (WebGLContext.environment)
 * @param {THREE.Texture} [labelMap]  label artwork for the label mesh, mapped by disc position (the UVs are an atlas)
 */
export default class VinylMaterial extends THREE.MeshPhysicalMaterial {
	constructor({ maps, scale, environmentMap = null, labelMap = null, ...vinyl }) {
		super({
			...maps,
			metalness: 1,
			roughness: 1,
			normalScale: maps.normalScale.clone().multiplyScalar(2),
			anisotropy: 0.4,
			envMap: environmentMap,
			envMapIntensity: 0.2, // the room is bright and the vinyl mirrors all of it; keep the marble legible
		})

		this.vinyl = {
			uScale: { value: scale },
			uVinyl: { value: bakeVinylTexture(vinyl) },
			uLabel: { value: labelMap },
			uLabelTransform: { value: labelMap?.matrix ?? new THREE.Matrix3() }, // the cover-fit crop
		}
		if (labelMap) this.defines.USE_LABEL = '' // keep STANDARD + PHYSICAL from the superclass
	}

	onBeforeCompile(shader) {
		Object.assign(shader.uniforms, this.vinyl)

		shader.vertexShader = shader.vertexShader
			.replace(
				'#include <common>',
				'uniform float uScale;\nvarying vec2 vDisc;\nvarying vec3 vRadial;\n#include <common>'
			)
			.replace(
				'#include <begin_vertex>',
				'#include <begin_vertex>\nvDisc = position.xy * uScale;\nvRadial = normalize(normalMatrix * vec3(normalize(position.xy), 0.0));'
			)

		shader.fragmentShader = shader.fragmentShader
			.replace(
				'#include <common>',
				`varying vec2 vDisc;\nvarying vec3 vRadial;\nuniform sampler2D uVinyl;\nuniform sampler2D uLabel;\nuniform mat3 uLabelTransform;\n#include <common>\n${groovesChunk}\n${paperChunk}`
			)
			.replace(
				'#include <color_fragment>',
				`#include <color_fragment>
				vec4 vinyl = texture2D(uVinyl, vDisc + 0.5);
				diffuseColor.rgb = vinyl.rgb;
				float onLabel = 0.0; // 1 on the paper label (USE_LABEL), else 0
				#ifdef USE_LABEL
					vec2 labelUv = vDisc / (2.0 * LABEL) + 0.5;
					if (!gl_FrontFacing) labelUv.x = 1.0 - labelUv.x;
					labelUv = (uLabelTransform * vec3(labelUv, 1.0)).xy;
					onLabel = smoothstep(LABEL + fwidth(length(vDisc)), LABEL, length(vDisc));
					vec3 print = texture2D(uLabel, labelUv).rgb * (1.0 + (paper(vDisc) - 0.5) * 2.0 * PAPER_GRAIN);
					diffuseColor.rgb = mix(diffuseColor.rgb, print, onLabel);
				#endif`
			)
			// a roughness floor spreads the ORM's mirror finish into a sheen; the paper label is matte, not metal
			.replace(
				'#include <roughnessmap_fragment>',
				'#include <roughnessmap_fragment>\nroughnessFactor = mix(max(roughnessFactor, 0.225), PAPER_ROUGHNESS, onLabel);'
			)
			.replace(
				'#include <metalnessmap_fragment>',
				'#include <metalnessmap_fragment>\nmetalnessFactor = mix(metalnessFactor, 0.0, onLabel);'
			)
			// darken the groove walls so the grooves read as lines, not just sheen: from the normal map's
			// tilt up close, and from the procedural ridges once the map has mip-filtered flat
			.replace(
				'#include <normal_fragment_maps>',
				'#include <normal_fragment_maps>\nnormal = grooves(normal, vRadial, vDisc);\ndiffuseColor.rgb *= 1.0 - GROOVE_SHADE * max(grooveShade(vDisc), smoothstep(0.02, 0.8, length(mapN.xy)));'
			)
			.replace(
				'#include <lights_physical_fragment>',
				// rough across the grooves (radial), smooth along them
				'#include <lights_physical_fragment>\nmaterial.anisotropyB = normalize(cross(normal, vRadial));\nmaterial.anisotropyT = normalize(cross(material.anisotropyB, normal));'
			)
	}
}
